import { config } from '../../config/config';
import { getHasuraClaimsFromToken, getStoredIdToken } from '../../authToken';
import { ADMIN_PORTAL_ALLOWED } from '../../helper';
import { buildTicketStatsFromPlacements } from './goals.breakdown';

export type { BreakdownTotals, MonthlyBreakdownStats, SaleBreakdown, TicketStatsResult } from './goals.breakdown';
export { formatBreakdownCurrency, resolveQaGrossProfit } from './goals.breakdown';

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');
const GRAPHQL_URL = trimTrailingSlash(process.env.REACT_APP_BACKEND_URL || config.backend_url || '');

/** Match index.tsx / Apollo auth — sub_admin must be sent or Hasura falls back to "user" and returns no sold tickets. */
const resolveHasuraRoleHeader = (token: string): string => {
  const claims = getHasuraClaimsFromToken(token);
  if (!claims) return '';

  const allowedRoles = Array.isArray(claims?.['x-hasura-allowed-roles'])
    ? claims['x-hasura-allowed-roles']
    : [];
  const defaultRole = claims?.['x-hasura-default-role'] || '';
  const requestedRole = localStorage.getItem('x-hasura-user-role') || '';

  if (
    requestedRole &&
    ADMIN_PORTAL_ALLOWED.includes(requestedRole) &&
    allowedRoles.includes(requestedRole)
  ) {
    return requestedRole;
  }

  if (defaultRole && allowedRoles.includes(defaultRole)) {
    return defaultRole;
  }

  return '';
};

const authHeaders = (): Record<string, string> => {
  const token = getStoredIdToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (token) headers.Authorization = `Bearer ${token}`;

  if (token) {
    const hasuraRole = resolveHasuraRoleHeader(token);
    if (hasuraRole) {
      headers['x-hasura-role'] = hasuraRole;
    }
  }

  return headers;
};

const executeGraphQL = async <T = any>(query: string, variables: Record<string, any> = {}): Promise<T> => {
  if (!GRAPHQL_URL) {
    throw new Error('Missing GraphQL URL configuration');
  }

  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ query, variables }),
  });

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }
  if (payload?.errors?.[0]?.message) {
    throw new Error(payload.errors[0].message);
  }

  return payload?.data as T;
};

const GET_ANNUAL_GOALS = `
query GetAnnualGoals($year: Int!) {
  annual_goals(where: {year: {_eq: $year}}) {
    id
    year
    revenue_goal
    profit_goal
    actual_revenue
    actual_profit
    created_at
    updated_at
  }
}
`;

const UPSERT_ANNUAL_GOALS = `
mutation UpsertAnnualGoals($year: Int!, $revenue_goal: numeric!, $profit_goal: numeric!) {
  insert_annual_goals_one(
    object: {year: $year, revenue_goal: $revenue_goal, profit_goal: $profit_goal}
    on_conflict: {
      constraint: annual_goals_year_key
      update_columns: [revenue_goal, profit_goal, updated_at]
    }
  ) {
    id
    year
    revenue_goal
    profit_goal
    actual_revenue
    actual_profit
    created_at
    updated_at
  }
}
`;

const GET_MONTHLY_PERFORMANCE = `
query GetMonthlyPerformance($year: Int!) {
  monthly_performance(where: {year: {_eq: $year}}, order_by: {month: asc}) {
    id
    year
    month
    revenue
    profit
    goal_amount
    profit_goal
    leagues
    events
    created_at
    updated_at
  }
}
`;

const UPSERT_MONTHLY_PERFORMANCE = `
mutation UpsertMonthlyPerformance(
  $year: Int!,
  $month: Int!,
  $revenue: numeric!,
  $profit: numeric!,
  $goal_amount: numeric!,
  $profit_goal: numeric!,
  $leagues: [String!]!,
  $events: [String!]!
) {
  insert_monthly_performance_one(
    object: {
      year: $year,
      month: $month,
      revenue: $revenue,
      profit: $profit,
      goal_amount: $goal_amount,
      profit_goal: $profit_goal,
      leagues: $leagues,
      events: $events
    }
    on_conflict: {
      constraint: monthly_performance_year_month_key
      update_columns: [revenue, profit, goal_amount, profit_goal, leagues, events, updated_at]
    }
  ) {
    id
    year
    month
    revenue
    profit
    goal_amount
    profit_goal
    leagues
    events
    created_at
    updated_at
  }
}
`;

const GET_MAJOR_EVENTS = `
query GetMajorEvents {
  major_events(order_by: {event_date: asc}) {
    id
    name
    event_date
    created_at
    updated_at
  }
}
`;

const INSERT_MAJOR_EVENT = `
mutation InsertMajorEvent($name: String!, $event_date: timestamptz) {
  insert_major_events_one(object: {name: $name, event_date: $event_date}) {
    id
    name
    event_date
    created_at
    updated_at
  }
}
`;

const GET_ACTIVE_LEAGUES_FOR_GOALS = `
query GetActiveLeaguesForGoals {
  leagues(
    where: {
      l_deleted_at: {_is_null: true},
      l_is_active: {_eq: true}
    },
    order_by: {l_name: asc}
  ) {
    l_id
    l_name
  }
}
`;

const GET_MARKETPLACES_FOR_GOALS = `
query GetMarkeplaces {
  marketplaces(order_by: {name: asc}) {
    id
    name
    vl_fee_percentage
    active
  }
}
`;

/** Sold tickets — rep via ticket.user (tickets.u_id); placement.u_id is often null/stale. */
const GET_SOLD_TICKET_REVENUE_BY_YEAR = `
query GetSoldTicketRevenueByYear($startDate: timestamptz!, $endDate: timestamptz!) {
  ticket_placement(
    where: {
      tp_status: {_eq: Sold}
      tp_is_support_vanderbilt_nil_fund: {_eq: false}
      tp_sold_at: {_gte: $startDate, _lt: $endDate}
    }
  ) {
    tp_id
    tp_sold_at
    tp_logitix_amount
    tp_sold_amount
    tp_quick_cut_amount
    tp_marketplace_fee_percent
    tp_marketplace_name
    tp_execution_type
    tp_section
    tp_row
    tp_seat_no
    ticket {
      user {
        u_full_name
        u_email_id
        sales_reps {
          sr_commission_rate
        }
      }
    }
  }
}
`;

/** Optional — affiliate rows; skipped when role lacks tp_referral_details permission. */
const GET_REFERRAL_PAYOUTS_BY_YEAR = `
query GetReferralPayoutsByYear($startDate: timestamptz!, $endDate: timestamptz!) {
  ticket_placement(
    where: {
      tp_status: {_eq: Sold}
      tp_is_support_vanderbilt_nil_fund: {_eq: false}
      tp_sold_at: {_gte: $startDate, _lt: $endDate}
      tp_referral_details: { referral_payout: { _gt: 0 } }
    }
  ) {
    tp_id
    tp_referral_details {
      referral_payout
      referral_percentage
      referral_data
      referral_bonus {
        rb_type
      }
    }
  }
}
`;

// ── Annual Goals ────────────────────────────────────────────────────────────
export const fetchAnnualGoals = async (year: number) => {
  try {
    const data = await executeGraphQL<{ annual_goals?: any[] }>(GET_ANNUAL_GOALS, { year });
    const goal = data?.annual_goals?.[0] || null;
    if (!goal) return null;
    return {
      ...goal,
      actual_revenue: Number(goal.actual_revenue || 0),
      actual_profit: Number(goal.actual_profit || 0),
    };
  } catch (error: any) {
    const message = String(error?.message || "").toLowerCase();
    if (message.includes("field 'annual_goals' not found")) {
      return null;
    }
    throw error;
  }
};

export const upsertAnnualGoals = async (year: number, revenue_goal: number, profit_goal: number) => {
  const data = await executeGraphQL<{ insert_annual_goals_one?: any }>(UPSERT_ANNUAL_GOALS, {
    year,
    revenue_goal,
    profit_goal,
  });
  return data?.insert_annual_goals_one || null;
};

// ── Monthly Performance ──────────────────────────────────────────────────────
export const fetchMonthlyPerformance = async (year: number) => {
  try {
    const data = await executeGraphQL<{ monthly_performance?: any[] }>(GET_MONTHLY_PERFORMANCE, { year });
    return data?.monthly_performance || [];
  } catch (error: any) {
    const message = String(error?.message || "").toLowerCase();
    if (message.includes("field 'monthly_performance' not found")) {
      return [];
    }
    throw error;
  }
};

export const upsertMonthlyPerformance = async (
  year: number,
  month: number,
  revenue: number,
  profit: number,
  goal_amount: number,
  profit_goal: number,
  leagues: string[],
  events: string[],
) => {
  const data = await executeGraphQL<{ insert_monthly_performance_one?: any }>(UPSERT_MONTHLY_PERFORMANCE, {
    year,
    month,
    revenue,
    profit,
    goal_amount,
    profit_goal,
    leagues,
    events,
  });
  return data?.insert_monthly_performance_one || null;
};

// ── Major Events ──────────────────────────────────────────────────────────────
export const fetchMajorEvents = async () => {
  try {
    const data = await executeGraphQL<{ major_events?: any[] }>(GET_MAJOR_EVENTS);
    return data?.major_events || [];
  } catch (error: any) {
    const message = String(error?.message || "").toLowerCase();
    // Older Hasura schemas may not expose major_events table.
    // Goals page should still render using available datasets.
    if (message.includes("field 'major_events' not found")) {
      return [];
    }
    throw error;
  }
};

export const addMajorEvent = async (name: string, event_date?: string) => {
  const data = await executeGraphQL<{ insert_major_events_one?: any }>(INSERT_MAJOR_EVENT, {
    name,
    event_date: event_date || null,
  });
  return data?.insert_major_events_one || null;
};

// ── Active Leagues ─────────────────────────────────────────────────────────────
export const fetchActiveLeagues = async () => {
  const data = await executeGraphQL<{ leagues?: Array<{ l_name?: string }> }>(GET_ACTIVE_LEAGUES_FOR_GOALS);
  return (data?.leagues || []).map((league) => league.l_name).filter(Boolean);
};

// ── Marketplaces (Dynamic Fee Configuration) ──────────────────────────────────
export const fetchMarketplaces = async () => {
  const data = await executeGraphQL<{
    marketplaces?: Array<{ 
      id: string;
      name: string; 
      vl_fee_percentage: number;
      active: boolean;
    }>;
  }>(GET_MARKETPLACES_FOR_GOALS);
  
  return data?.marketplaces || [];
};

const mergeReferralDetails = (
  placements: Array<Record<string, unknown>>,
  referralRows: Array<{ tp_id?: string; tp_referral_details?: unknown[] }>,
) => {
  const byTpId = new Map(
    referralRows
      .filter((row) => row?.tp_id)
      .map((row) => [row.tp_id as string, row.tp_referral_details || []]),
  );
  return placements.map((placement) => {
    const tpId = placement.tp_id as string | undefined;
    if (!tpId || !byTpId.has(tpId)) return placement;
    return { ...placement, tp_referral_details: byTpId.get(tpId) };
  });
};

// ── Ticket Stats — full per-sale breakdown aggregated by month ───────────────
export const fetchTicketStats = async (
  year: number,
  dateRange?: { startDate: string; endDate: string },
) => {
  const range = dateRange || {
    startDate: `${year}-01-01T00:00:00Z`,
    endDate: `${year + 1}-01-01T00:00:00Z`,
  };

  const ticketData = await executeGraphQL<{
    ticket_placement?: Parameters<typeof buildTicketStatsFromPlacements>[0];
  }>(GET_SOLD_TICKET_REVENUE_BY_YEAR, range);

  let placements: Array<Record<string, unknown>> = ticketData?.ticket_placement || [];

  try {
    const referralData = await executeGraphQL<{
      ticket_placement?: Array<{ tp_id?: string; tp_referral_details?: unknown[] }>;
    }>(GET_REFERRAL_PAYOUTS_BY_YEAR, range);
    placements = mergeReferralDetails(placements, referralData?.ticket_placement || []);
  } catch {
    // Role may lack tp_referral_details — revenue/profit still load; affiliate stays 0.
  }

  return buildTicketStatsFromPlacements(
    placements as Parameters<typeof buildTicketStatsFromPlacements>[0],
    year,
    dateRange,
  );
};
